import React, {useEffect} from 'react';
import {PageItem, TextBoxDataArrayType} from '@taopix/taopix-core/dist/types/types';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  LoadingSpinnerIcon,
  TextInput,
  Theme,
  ThemeName,
} from '@taopix/taopix-design-system';
import {useTranslation} from 'react-i18next';
import {useAssistant} from '../../../Context/Assistant/AssistantContext';
import {
  AutoFitMode,
  Common,
  Lang,
  NumBool,
  Text,
  AUTOFIT_TEXT_SCALE_RESOLUTION,
  TextStoreKeys,
  BoxType,
} from '@taopix/taopix-core';
import {ProcessedTextData} from '@taopix/taopix-core/dist/types/Service/TextService';
import {AssistantActions} from '../../../Context/Assistant/AssistantActionTypes';
import {useTaopix} from '../../../Context/TaopixContext';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import {CoverMode, CoverTextSubStepNegativeAction} from '../CreateCoverStep';
import {KeyboardKeys} from '../../../../Enums';
import {getText} from '../../../../Utils/Text';

type CoverTextSubStepProps = {
  children?: any;
  open: boolean;
  coverMode: CoverMode;
  negativeAction: CoverTextSubStepNegativeAction;
  height?: number;
  width?: number;
  onClose: () => void;
  onAfterClose: () => void;
  onContinue: () => void;
};

const PageItemTextBox = ({pageItem}: {pageItem: PageItem}) => {
  const {t} = useTranslation(['assistants']);
  const mounted = React.useRef(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * Gets the textbox label from the short label.
   *
   * @param pageItem The textbox page item object.
   * @returns The shot label, or the default text.
   */
  const getLabel = (pageItem: PageItem): string => {
    if (Object.keys(pageItem?.labelshort || {}).length === 0) return t('str_LabelUntitledTextBox');
    return Common.getLocalisedLabel(pageItem.labelshort, true).text;
  };

  /**
   * Gets the errors from the result of the text flightcheck.
   *
   * @param pageItem Page item to read errors from.
   * @returns The error message or undefined if no errors.
   */
  const resolveErrorMessage = (pageItem: PageItem): string | undefined => {
    if (NumBool.True === pageItem.styledtext.textoverflowed) {
      return t('*:str_MessageTextOverflowedShort');
    }

    return undefined;
  };

  return (
    <TextInput
      id={`covertext-${pageItem.ref}`}
      name={`covertext-${pageItem.ref}`}
      defaultValue={getText(pageItem.styledtext.data)}
      label={getLabel(pageItem)}
      className={'tds-w-full'}
      error={resolveErrorMessage(pageItem)}
    />
  );
};

export const CoverTextSubStep = ({
  open,
  coverMode,
  height,
  width,
  negativeAction,
  onClose,
  onAfterClose,
  onContinue,
}: CoverTextSubStepProps) => {
  const assistant = useAssistant();
  const ui = useUiState();
  const taopix = useTaopix();
  const {t} = useTranslation(['assistants']);
  const [serverCallInProgress, setServerCallInProgress] = React.useState(false);
  const formRef = React.useRef<HTMLFormElement>(null);

  /**
   * Run a "flightcheck" on the text the user has entered.
   *
   * @param payload Contains box and item data to send for processing.
   * @returns The result from textproc.
   */
  const flightCheckText = async (payload: {
    box: TextBoxDataArrayType;
    item: {name: string; metadata: string}[];
  }): Promise<ProcessedTextData[]> => {
    return (await Text.fetchBassData(
      {
        browserLanguageCode: Lang.getCode(),
        engineConfiguration: Text.store.get(TextStoreKeys.TEXT_ENGINE_CONFIG),
        box: payload.box,
        item: payload.item,
      },
      {arrayResponse: true}
    )) as ProcessedTextData[];
  };

  /**
   * Sanitise user input.
   *
   * @param text Text to sanitise.
   * @returns The sanitised string.
   */
  const clean = (text: string) => {
    return text.replace(/\r\n/g, '\r').replace(/\n/g, '\r').replace(/\t/g, '    ');
  };

  /**
   * Checks if any of the page items have an error.
   *
   * @param pageItems List of page items to check.
   * @returns True if at least 1 page item has an error.
   */
  const hasTextError = (pageItems: PageItem[]): boolean => {
    return pageItems.some(
      pageItem =>
        BoxType.Text === pageItem.type && NumBool.True === pageItem.styledtext.textoverflowed
    );
  };

  /**
   * Event to fire when the text has changed in the input.
   */
  const onSubmit = (): Promise<boolean> => {
    let textData: {box: any[]; item: {metadata: string; name: string}[]} = {box: [], item: []};
    // Store the position of the page items in the list
    // so we can map the results from textproc back to the correct page item.
    const positionMap = new Map();
    const deepClone = taopix.core.get('utils').deepClone;

    if (formRef.current instanceof HTMLFormElement) {
      const processTime = new Date().getTime();

      textData = Array.from(formRef.current.querySelectorAll('input'), (input, index) => {
        const pageItem = deepClone(assistant.state.coverItems[coverMode].textBox[index]);
        const text: string = clean(input.value);

        if (pageItem.styledtext.data.split('>').pop() === text) {
          return false;
        }

        const unflattenedStyledText = Text.unflattenText(
          pageItem.styledtext.data,
          pageItem.styledtext.defaultstyledata,
          {},
          {},
          pageItem.labelusedefaulttextstyle
        );

        pageItem.text = text;

        const boxData = {
          autofitmode:
            AutoFitMode.Off === pageItem.styledtext.autofitmode
              ? AutoFitMode.ShrinkToFit
              : pageItem.styledtext.autofitmode,
          autofitscale: AUTOFIT_TEXT_SCALE_RESOLUTION,
          boxheight: pageItem.boxheight,
          boxwidth: pageItem.boxwidth,
          data: Text.flattenText(unflattenedStyledText, text).data,
          direction: pageItem.styledtext.direction,
          marginbottom: pageItem.styledtext.marginbottom,
          marginleft: pageItem.styledtext.marginleft,
          marginright: pageItem.styledtext.marginright,
          margintop: pageItem.styledtext.margintop,
          missingfonts: pageItem.styledtext.missingfonts,
          missingglyphfonts: pageItem.styledtext.missingglyphfonts,
          origstyledtextautofitmode: pageItem.styledtext.origstyledtextautofitmode,
          performfontfallback: NumBool.True,
          processdefaultlabel: 0,
          textoverflowed: pageItem.styledtext.textoverflowed,
          verticalalignment: pageItem.styledtext.verticalalignment,
          processtime: processTime,
        };

        return {
          ref: pageItem.ref,
          box: boxData,
          item: {
            metadata: pageItem.metadata,
            name: pageItem.name,
          },
        };
      }).reduce(
        (
          accumulator: {
            box: TextBoxDataArrayType;
            item: {name: string; metadata: string}[];
          },
          current,
          index
        ) => {
          if (typeof current === 'boolean') return accumulator;

          accumulator.box.push(current.box);
          accumulator.item.push(current.item);
          const newIndex = accumulator.box.length - 1;
          positionMap.set(current.ref, newIndex);
          return accumulator;
        },
        {box: [], item: []}
      );
    }

    if (textData.item.length === 0) {
      return new Promise(resolve => {
        resolve(hasTextError(assistant.state.coverItems[coverMode].textBox));
      });
    }

    return flightCheckText(textData).then(response => {
      const pageItems = deepClone(assistant.state.coverItems[coverMode].textBox);
      let hasError = false;
      let checkAllPageItems = false;

      // Build response data.
      const updatedPageItemsData = pageItems.map((pageItem, index) => {
        const updateIndex = positionMap.get(pageItem.ref);

        if (undefined === updateIndex) {
          checkAllPageItems = true;
          return pageItem;
        }

        const responseData = response[updateIndex];

        if (responseData.textoverflowed && !hasError) {
          hasError = true;
        }

        return {
          ...pageItem,
          styledtext: {
            ...pageItem.styledtext,
            autofitmode: textData.box[updateIndex].autofitmode,
            bassdata: responseData.vectordata,
            data: textData.box[updateIndex].data,
            textoverflowed: responseData.textoverflowed ? 1 : 0,
            missingfonts: responseData.missingfonts,
            missingglyphfonts: responseData.missingglyphfonts,
            autofitscale: responseData.autofitscale,
          },
        };
      });

      // Second pass for errors in case a text box with an error wasn't changed.
      if (!hasError && checkAllPageItems) {
        hasError = hasTextError(updatedPageItemsData);
      }

      // Submit all changed at once.
      assistant.dispatch(
        AssistantActions.updateCoverPageItems({
          key: 'textBox',
          coverMode: coverMode,
          pageItems: updatedPageItemsData,
        })
      );

      return hasError;
    });
  };

  const getTitle = () => {
    return CoverMode.Back === coverMode ? t('*:str_LabelBackCover') : t('*:str_LabelFrontCover');
  };

  const onContinueSubmit = () => {
    if (serverCallInProgress) return;

    setServerCallInProgress(true);
    onSubmit()
      .then(hasError => {
        if (hasError) {
          // Scroll to the first error.
          // Delay a little to allow the Ui to re-render first.
          setTimeout(() => {
            if (formRef.current instanceof HTMLFormElement) {
              // Scroll to the label element.
              formRef.current
                .querySelector('.tds-theme-warning')
                ?.previousElementSibling?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                });
            }
          }, 1);
        } else {
          onContinue();
        }
      })
      .finally(() => {
        setServerCallInProgress(false);
      });
  };

  return (
    <>
      <Dialog
        id={`assistant-covertext-${coverMode}-substep`}
        open={open}
        height={height}
        width={width}
        afterClose={() => onAfterClose()}
        focusFirstElement
        theme={'tpx-theme-dialogs'}
        themeTip="Dialogs"
        title={getTitle()}
        onClose={onClose}
      >
        <DialogContent className="tds-space-y-md">
          <p>{t('str_LabelEnterCoverText')}</p>
          <Theme
            name={ThemeName.Container}
            allowBorder={false}
            allowCorners={false}
            className={'flex-1 p-lg space-y-lg'}
          >
            <form
              id={`cover-form-${coverMode}`}
              name={`cover-form-${coverMode}`}
              key={`cover-form-${coverMode}`}
              ref={formRef}
              onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
                event.preventDefault();
                return false;
              }}
              onKeyDown={(event: any) => {
                if (event.key === KeyboardKeys.ENTER) {
                  onContinueSubmit();
                }
              }}
              className="py-sm space-y-lg"
            >
              {assistant.state.coverItems[coverMode].textBox.map((textBox: PageItem) => {
                return (
                  <PageItemTextBox key={`pageitem-textbox-${textBox.ref}`} pageItem={textBox} />
                );
              })}
            </form>
          </Theme>
        </DialogContent>
        <DialogFooter
          alignment={!ui.state.isSmallScreen ? 'between' : 'auto'}
          isSmallScreen={ui.state.isSmallScreen}
        >
          <Button
            buttonStyle={ui.state.isSmallScreen ? 'link' : 'negative'}
            label={
              CoverTextSubStepNegativeAction.Back === negativeAction
                ? t('*:str_LabelBack')
                : t('*:str_ButtonClose')
            }
            onClick={onClose}
          />
          <Button
            buttonStyle="primary"
            label={t('*:str_ButtonContinue')}
            onClick={onContinueSubmit}
            startIcon={serverCallInProgress ? <LoadingSpinnerIcon /> : null}
            disabled={serverCallInProgress}
          />
        </DialogFooter>
      </Dialog>
    </>
  );
};
